import { create } from "zustand";
import { ProjectStage } from "../../../interfaces/Projects/Catalogs/projectStages";

// Función para comparar valores numéricos en cadenas
const compareOrderHint = (a: string, b: string): number => {
    return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
    });
};

// Función para ordenar las etapas por el campo project_stage_consecutive
const sortByConsecutive = (data: ProjectStage[]): ProjectStage[] => {
    return data.sort((a, b) =>
        compareOrderHint(a.project_stage_consecutive || "", b.project_stage_consecutive || "")
    );
};

interface ProjectStagesStore {
    projectStages: ProjectStage[];
    isStageCompleted: Record<number, boolean>;
    setProjectStages: (by: ProjectStage[]) => void;
    addProjectStage: (by: ProjectStage) => void;
    updateProjectStage: (by: ProjectStage) => void;
    deleteProjectStage: (by: number) => void;
    setIsStageCompleted: (stageId: number, completed: boolean) => void;
    checkIfTaskIsCompleted: (stage: ProjectStage) => boolean;
}


export const useProjectStagesStore = create<ProjectStagesStore>()(
    (set, get) => ({
        projectStages: [],
        isStageCompleted: {},
        setProjectStages: (by: ProjectStage[]) => {
            if (by.length === 0) {
                return;
            }
            const isStageCompleted = by.reduce((acc, projectStage) => {
                if (projectStage.project_stage_id) {
                    acc[projectStage.project_stage_id] = projectStage.is_done;
                }

                return acc;
            }, {} as Record<number, boolean>)

            const sortedStages = sortByConsecutive(by);

            set({ projectStages: sortedStages, isStageCompleted })
        },
        addProjectStage: (by: ProjectStage) => {
            const sortedStages = sortByConsecutive([...get().projectStages, by])
            set({ projectStages: sortedStages })
        },
        updateProjectStage: (by: ProjectStage) => {
            // Verificar si el elemento ya existe en projectStages
            const exists = get().projectStages.some(
                (projectStage) => projectStage.project_stage_id === by.project_stage_id
            );

            let stagesUpdated;
            if (exists) {
                // Si existe, actualiza el elemento
                stagesUpdated = get().projectStages.map((projectStage) =>
                    projectStage.project_stage_id === by.project_stage_id ? by : projectStage
                );
            } else {
                // Si no existe, agrega el nuevo elemento
                stagesUpdated = [...get().projectStages, by];
            }

            const sortedStages = sortByConsecutive(stagesUpdated);
            set({ projectStages: sortedStages })
            if (by.project_stage_id) {
                get().setIsStageCompleted(by.project_stage_id, by.is_done)
            }

        },
        deleteProjectStage: (by: number) => {
            const stagesUpdated = get().projectStages.filter((projectStage) => projectStage.project_stage_id != by);
            const sortedStages = sortByConsecutive(stagesUpdated);
            set({ projectStages: sortedStages })
        },
        setIsStageCompleted: (stageId, completed) => {
            set((state) => ({
                isStageCompleted: {
                    ...state.isStageCompleted,
                    [stageId]: completed
                }
            }))
        },
        checkIfTaskIsCompleted: (stage: ProjectStage) => {
            if (stage.project_stage_id) {
                const stageState = get().isStageCompleted[stage.project_stage_id];
                return stageState ?? false;
            }
            return false;
        }
    })
)